import { formatMoney } from '@theme/utilities';

class StickyAddToCart extends HTMLElement {
  connectedCallback() {
    this.form = this.closest('product-form');
    this.section = this.closest('.shopify-section');
    this.content = this.querySelector('.sticky-add-to-cart__content');
    this.template = this.section?.querySelector('[data-sc-sticky-template]');
    if (!this.form || !this.content) return;
    if (!this.template) {
      this.connectLegacy();
      return;
    }
    this.sectionId = this.template.dataset.sectionId;
    this.abort = new AbortController();
    this.pending = false;
    this.dataset.scReady = 'true';
    this.bar = this.createBar();
    this.append(this.bar);
    this.bar.getBoundingClientRect();
    this.observer = new IntersectionObserver(() => this.updateVisibility(), { threshold: [0, 1] });
    this.footer = document.querySelector('.sc-footer, [data-ref="footer"]');
    if (this.footer) this.observer.observe(this.footer);
    this.buttonObserver = new MutationObserver(() => {
      this.bindButton();
      this.sync();
    });
    this.buttonObserver.observe(this.content, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'class'] });

    this.form.addEventListener('variant:selected', () => {
      this.pending = true;
      this.sync();
    }, { signal: this.abort.signal });
    this.form.addEventListener('variant:updated', (event) => {
      this.pending = false;
      this.variant = event.detail?.resource;
      this.sync(event.detail?.resource?.price);
      this.bindButton();
    }, { signal: this.abort.signal });
    this.form.addEventListener('form:updated', (event) => {
      this.bindButton();
      this.sync(event.detail?.prices?.price);
    }, { signal: this.abort.signal });
    document.addEventListener('sc:size-guide-open', (event) => this.guideOpened(event), { signal: this.abort.signal });
    document.addEventListener('sc:size-guide-selection', (event) => this.guideSelected(event), { signal: this.abort.signal });
    document.addEventListener('sc:size-guide-close', (event) => this.guideClosed(event), { signal: this.abort.signal });
    document.addEventListener('pointerdown', (event) => {
      for (const bar of this.bars()) {
        if (!bar.contains(event.target)) this.closeOptions(bar);
      }
    }, { signal: this.abort.signal });
    window.addEventListener('resize', () => this.updateVisibility(), { passive: true, signal: this.abort.signal });

    this.bindButton();
    this.sync();
    this.updateVisibility();
    this.guideDialog = this.section.querySelector('dialog[data-section-id]');
    if (this.guideDialog) {
      this.dialogObserver = new MutationObserver(() => {
        if (!this.guideDialog.open) this.hideGuideBar();
        this.updateVisibility();
      });
      this.dialogObserver.observe(this.guideDialog, { attributes: true, attributeFilter: ['open'] });
      if (this.guideDialog.open && this.guideDialog.dataset.scGuideSelected === 'true') this.showGuideBar();
    }
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.observer?.disconnect();
    this.buttonObserver?.disconnect();
    this.dialogObserver?.disconnect();
    this.legacyFooterObserver?.disconnect();
    clearTimeout(this.legacyClosingTimeout);
    clearTimeout(this.guideHideTimeout);
    this.bar?.remove();
    this.guideBar?.remove();
    delete this.dataset.scReady;
  }

  bars() { return [this.bar, this.guideBar].filter(Boolean); }

  connectLegacy() {
    this.legacyFormInView = true;
    this.legacyFooterInView = false;
    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.style.minHeight = String(this.offsetHeight) + 'px';
      this.observer = new IntersectionObserver(([entry]) => {
        this.legacyFormInView = entry.isIntersecting;
        this.updateLegacy();
      });
      this.observer.observe(this.form);
      const footer = document.querySelector('.sc-footer, [data-ref="footer"]');
      if (!footer) return;
      this.legacyFooterObserver = new IntersectionObserver(([entry]) => {
        this.legacyFooterInView = entry.isIntersecting;
        this.updateLegacy();
      });
      this.legacyFooterObserver.observe(footer);
    });
  }

  updateLegacy() {
    const active = !this.legacyFormInView && !this.legacyFooterInView;
    if (active === (this.dataset.active === 'true')) return;
    clearTimeout(this.legacyClosingTimeout);
    this.dataset.active = String(active);
    if (active) {
      delete this.dataset.closing;
      this.content.classList.add('color-scheme-1');
    } else {
      this.dataset.closing = 'true';
      this.legacyClosingTimeout = setTimeout(() => {
        delete this.dataset.closing;
        this.content.classList.remove('color-scheme-1');
      }, 300);
    }
  }

  createBar(inGuide = false) {
    const bar = this.template.content.firstElementChild.cloneNode(true);
    if (inGuide) bar.classList.add('sc-sticky-cart--guide');
    const panel = bar.querySelector('[data-sc-sticky-options]');
    const toggle = bar.querySelector('[data-sc-sticky-options-toggle]');
    panel.id = 'sc-sticky-options-' + this.sectionId + '-' + (inGuide ? 'guide' : 'page');
    toggle?.setAttribute('aria-controls', panel.id);
    bar.addEventListener('click', (event) => {
      const button = event.target.closest('button');
      if (!button) return;
      if (button.hasAttribute('data-sc-sticky-add')) this.addToCart(inGuide);
      if (button.hasAttribute('data-sc-sticky-options-toggle')) {
        const willOpen = panel.hidden;
        this.renderOptions(bar);
        panel.hidden = !willOpen;
        toggle.setAttribute('aria-expanded', String(willOpen));
      }
      if (button.hasAttribute('data-sc-sticky-options-close')) {
        this.closeOptions(bar);
        toggle?.focus({ preventScroll: true });
      }
      if (button.hasAttribute('data-sc-native-option')) this.selectOption(button, bar);
    });
    bar.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !panel.hidden) {
        event.preventDefault();
        event.stopPropagation();
        this.closeOptions(bar);
        toggle?.focus({ preventScroll: true });
      }
    });
    return bar;
  }

  bindButton() {
    const next = this.content.querySelector('[data-ref="add-to-cart-button"]');
    if (next === this.mainButton) return;
    if (this.mainButton) this.observer.unobserve(this.mainButton);
    this.mainButton = next;
    if (next) this.observer.observe(next);
    this.updateVisibility();
  }

  nativeGroups() {
    const picker = this.form.querySelector('variant-picker');
    if (!picker) return [];
    return Array.from(picker.querySelectorAll('.variant-picker__option')).map((group) => {
      const select = group.querySelector('select');
      const inputs = select ? Array.from(select.options) : Array.from(group.querySelectorAll('input[type="radio"]'));
      const name = (select?.name || inputs[0]?.name || '').replace(/^option-/, '');
      return { name, select, inputs };
    }).filter((group) => group.inputs.length);
  }

  selectedValues() {
    return this.nativeGroups().map((group) => {
      const chosen = group.select ? group.select.selectedOptions[0] : group.inputs.find((input) => input.checked);
      return chosen ? group.name + ' ' + chosen.value : '';
    }).filter(Boolean).join(' · ');
  }

  renderOptions(bar) {
    const container = bar.querySelector('[data-sc-sticky-option-groups]');
    const activeValue = container.contains(document.activeElement) ? document.activeElement?.dataset.scNativeOption : null;
    const fragment = document.createDocumentFragment();
    for (const group of this.nativeGroups()) {
      const wrapper = document.createElement('div');
      wrapper.className = 'sc-sticky-cart__option-group';
      const label = document.createElement('p');
      label.className = 'sc-sticky-cart__option-label';
      label.textContent = group.name;
      const values = document.createElement('div');
      values.className = 'sc-sticky-cart__option-values';
      for (const input of group.inputs) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'sc-sticky-cart__option-value';
        button.dataset.scNativeOption = input.dataset.optionValueId || '';
        button.textContent = input.value;
        button.disabled = input.disabled || input.getAttribute('aria-disabled') === 'true';
        button.setAttribute('aria-pressed', String(group.select ? input.selected : input.checked));
        values.append(button);
      }
      wrapper.append(label, values);
      fragment.append(wrapper);
    }
    container.replaceChildren(fragment);
    if (activeValue) Array.from(container.querySelectorAll('button')).find((button) => button.dataset.scNativeOption === activeValue)?.focus({ preventScroll: true });
  }

  selectOption(button, bar) {
    const picker = this.form.querySelector('variant-picker');
    const native = Array.from(picker?.querySelectorAll('[data-option-value-id]') || []).find((option) => option.dataset.optionValueId === button.dataset.scNativeOption);
    if (!native || native.disabled || native.getAttribute('aria-disabled') === 'true') return;
    if (native instanceof HTMLOptionElement) {
      native.parentElement.value = native.value;
      native.parentElement.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      native.click();
    }
    this.sync();
    this.closeOptions(bar);
    bar.querySelector('[data-sc-sticky-options-toggle]')?.focus({ preventScroll: true });
  }

  sync(price) {
    if (!this.mainButton) return;
    const variantJson = this.form.querySelector('variant-picker script[type="application/json"]')?.textContent;
    if (variantJson) {
      try { this.variant = JSON.parse(variantJson); } catch {}
    }
    const imageSrc = this.variant?.featured_media?.preview_image?.src || this.variant?.featured_image?.src;
    const imageFallback = this.template.content.querySelector('.sc-sticky-cart__image');
    const amount = Number.isFinite(price) ? price : this.form.getCurrentTotalPrice?.()?.price ?? parseInt(this.mainButton.closest('[data-ref="add-to-cart-button-container"]')?.dataset.price, 10);
    for (const bar of this.bars()) {
      const image = bar.querySelector('.sc-sticky-cart__image');
      if (image) {
        if (imageSrc) {
          image.src = imageSrc;
          image.removeAttribute('srcset');
        } else if (imageFallback) {
          image.src = imageFallback.src;
          if (imageFallback.getAttribute('srcset')) image.setAttribute('srcset', imageFallback.getAttribute('srcset'));
        }
      }
      const summary = bar.querySelector('[data-sc-sticky-summary]');
      if (summary) summary.textContent = this.selectedValues();
      if (Number.isFinite(amount)) bar.querySelector('[data-sc-sticky-price]').innerHTML = formatMoney(amount);
      const proxy = bar.querySelector('[data-sc-sticky-add]');
      proxy.disabled = this.pending || this.mainButton.disabled || this.mainButton.classList.contains('is-loading');
      proxy.textContent = this.mainButton.disabled && !this.pending ? this.template.dataset.unavailable : this.template.dataset.addToCart;
      proxy.setAttribute('aria-busy', String(this.pending || this.mainButton.classList.contains('is-loading')));
      if (!bar.querySelector('[data-sc-sticky-options]').hidden) this.renderOptions(bar);
    }
  }

  updateVisibility() {
    if (!this.bar || !this.mainButton) return;
    const rect = this.mainButton.getBoundingClientRect();
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    const inView = rect.bottom > 0 && rect.top < viewportHeight && rect.right > 0 && rect.left < window.innerWidth && rect.width > 0 && rect.height > 0;
    const footerRect = this.footer?.getBoundingClientRect();
    const footerInView = footerRect && footerRect.bottom >= 0 && footerRect.top <= viewportHeight && footerRect.height > 0;
    const dialogOpen = this.guideDialog?.open || this.section.querySelector('dialog[data-section-id][open]');
    const visible = !inView && !dialogOpen && !footerInView;
    this.bar.dataset.visible = String(visible);
    this.bar.setAttribute('aria-hidden', String(!visible));
    this.bar.inert = !visible;
    this.dataset.active = String(visible);
    if (!visible) this.closeOptions(this.bar);
  }

  guideOpened(event) {
    if (event.detail?.sectionId !== this.sectionId) return;
    this.guideDialog = this.section.querySelector('dialog[data-section-id]');
    if (this.guideDialog) delete this.guideDialog.dataset.scGuideSelected;
    this.hideGuideBar();
    this.updateVisibility();
  }

  guideSelected(event) {
    if (event.detail?.sectionId !== this.sectionId) return;
    this.guideDialog = this.section.querySelector('dialog[data-section-id]');
    if (!this.guideDialog?.open) return;
    this.guideDialog.dataset.scGuideSelected = 'true';
    this.showGuideBar();
  }

  showGuideBar() {
    const slot = this.guideDialog?.querySelector('[data-sc-guide-purchase]');
    if (!slot) return;
    clearTimeout(this.guideHideTimeout);
    slot.hidden = false;
    if (!this.guideBar?.isConnected) {
      this.guideBar = this.createBar(true);
      slot.replaceChildren(this.guideBar);
    }
    this.sync();
    this.guideBar.getBoundingClientRect();
    this.guideBar.dataset.visible = 'true';
    this.guideBar.inert = false;
    this.guideBar.setAttribute('aria-hidden', 'false');
    this.updateVisibility();
  }

  hideGuideBar() {
    if (!this.guideBar) return;
    this.guideBar.dataset.visible = 'false';
    this.guideBar.inert = true;
    this.guideBar.setAttribute('aria-hidden', 'true');
    this.closeOptions(this.guideBar);
    clearTimeout(this.guideHideTimeout);
    this.guideHideTimeout = setTimeout(() => {
      const slot = this.guideDialog?.querySelector('[data-sc-guide-purchase]');
      if (this.guideBar.dataset.visible !== 'true' && slot) slot.hidden = true;
    }, 500);
  }

  guideClosed(event) {
    if (event.detail?.sectionId !== this.sectionId) return;
    if (this.guideDialog) delete this.guideDialog.dataset.scGuideSelected;
    this.hideGuideBar();
    this.updateVisibility();
  }

  closeOptions(bar) {
    const panel = bar?.querySelector('[data-sc-sticky-options]');
    if (!panel) return;
    panel.hidden = true;
    bar.querySelector('[data-sc-sticky-options-toggle]')?.setAttribute('aria-expanded', 'false');
  }

  addToCart(inGuide) {
    const native = this.content.querySelector('[data-ref="add-to-cart-button"]');
    if (!native || native.disabled || this.pending || native.classList.contains('is-loading')) return;
    native.click();
    this.sync();
    if (inGuide) document.dispatchEvent(new CustomEvent('sc:size-guide-request-close', { detail: { sectionId: this.sectionId, reason: 'add-to-cart' } }));
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}


